import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, Clock, Award, CheckCircle, AlertCircle, Users, Target } from 'lucide-react';
import api from '../services/api';
import { isAuthenticated } from '../utils/auth';

function ProgramDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await api.get(`/programs/${slug}`);
        setProgram(response.data);
      } catch (error) {
        console.error('Error fetching program:', error);
        setError('Program not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProgram();
  }, [slug]);

  const handleEnroll = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    setEnrolling(true);
    setError('');

    try {
      const response = await api.post('/enrollments', { programId: program._id });
      navigate(`/screening/${response.data._id}`);
    } catch (err) {
      const msg = err.response?.data?.message || '';

      // If backend says already enrolled, try to fetch existing enrollment
      if (msg.toLowerCase().includes('already') || err.response?.status === 409) {
        try {
          const myRes = await api.get('/enrollments/my');
          const existing = myRes.data.find(e => e.program?._id === program._id || e.program === program._id);
          if (existing) {
            // If enrollment is still screening-pending, let user take screening
            if (existing.enrollmentStatus === 'screening-pending') {
              navigate(`/screening/${existing._id}`);
            } else if (existing.enrollmentStatus === 'payment-pending') {
              // If payment is pending, send user to payment page
              navigate(`/payment/${existing._id}`);
            } else if (existing.enrollmentStatus === 'active' || existing.enrollmentStatus === 'completed') {
              // Only show 'Already enrolled' for active/completed enrollments
              setError('Already enrolled in this program');
            } else {
              // For any other statuses, navigate to learning path
              navigate(`/learning/${existing._id}`);
            }
            setEnrolling(false);
            return;
          }
        } catch (e) {
          // ignore and fallthrough to show default message
        }
      }

      setError(msg || 'Enrollment failed');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading program...</p>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-700">Program not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Header */}
      <div style={{marginTop: "73px"}} className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-full uppercase tracking-wide border border-white/30">
              {program.category}
            </span>
            <span className="px-4 py-1.5 bg-blue-500 text-white text-sm font-semibold rounded-full flex items-center gap-1">
              <Award className="w-4 h-4" />
              {program.meta?.level}
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">{program.title}</h1>
          <p className="text-xl text-slate-300 max-w-3xl">{program.description}</p>
          
          <div className="flex flex-wrap gap-6 mt-8">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-300" />
              <span className="text-slate-200">{program.durationMonths} months</span>
            </div>
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-slate-300" />
              <span className="text-slate-200">{program.sections?.length} sections</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-green-400">₹{program.price}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Program Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* What You'll Learn */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Target className="w-6 h-6" />
                What You Will Learn
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {program.whatYouWillLearn?.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Program Sections */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <BookOpen className="w-6 h-6" />
                Program Sections
              </h3>
              <div className="space-y-4">
                {program.sections?.map((section, idx) => (
                  <div 
                    key={section._id} 
                    className="bg-slate-50 border border-slate-200 rounded-lg p-5 hover:border-slate-300 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-slate-900 text-white rounded-lg flex items-center justify-center font-bold">
                        {section.order}
                      </div>
                      <div className="flex-1">
                        <h5 className="text-lg font-bold text-slate-900 mb-2">
                          {section.name}
                        </h5>
                        <p className="text-slate-600 mb-3">{section.description}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Clock className="w-4 h-4" />
                          <span>Estimated: {section.estimatedHours} hours</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Enrollment Card */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-6 sticky top-4">
              <h4 className="text-2xl font-bold text-slate-900 mb-4">Ready to Start?</h4>
              
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <div className="text-center">
                  <p className="text-sm text-slate-600 mb-1">Program Price</p>
                  <p className="text-4xl font-bold text-slate-900">₹{program.price}</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <button
                onClick={handleEnroll}
                disabled={enrolling}
                className="w-full py-4 px-6 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
              >
                {enrolling ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Enrolling...
                  </span>
                ) : (
                  'Enroll Now'
                )}
              </button>

              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>Lifetime access to content</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>Industry-ready curriculum</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>Certificate upon completion</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProgramDetail;
